import courseraPlusProductVariants from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus-common/utils/withExistingCourseraLiteSubscription';
import { useNaptime } from 'bundles/naptimejs';
import ProductPricesV3 from 'bundles/naptimejs/resources/productPrices.v3';
import { useGetProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { ProductType } from 'bundles/payments-common/common/ProductType';
import { COURSERA_PLUS_SUBSCRIPTION } from 'bundles/payments-common/common/ProductType';
import { useCart } from 'bundles/payments-common/utils/hooks';

type PropsFromProductPrices = {
  courseraPlusMonthlyAndAnnualPrices?: ProductPrices[];
  courseraPlusMonthlyAndAnnualPricesByCurrency?: ProductPrices[];
};

export const useGetSubscriptionTiersProductPrices = ({
  skip = false,
  shouldGetCourseraPlusAnnual = true,
}: {
  skip?: boolean;
  shouldGetCourseraPlusAnnual?: boolean;
}) => {
  const { existingCourseraLiteSubscription: liteSubscription } = useExistingCourseraLiteSubscription(skip);
  const { cart } = useCart({ cartId: liteSubscription?.cartId });
  const currencyData = {
    countryIsoCode: cart?.countryIsoCode,
    currencyCode: cart?.currencyCode,
  };
  const shouldFetchPriceByCurrency =
    currencyData?.currencyCode && liteSubscription?.stripeSubscriptionPaymentInformation;

  const products: { productType: ProductType; productItemId: string }[] = [
    {
      productType: COURSERA_PLUS_SUBSCRIPTION,
      productItemId: courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL,
    },
  ];

  if (shouldGetCourseraPlusAnnual) {
    products.push({
      productType: COURSERA_PLUS_SUBSCRIPTION,
      productItemId: courseraPlusProductVariants.ANNUAL_SEVEN_DAY_FREE_TRIAL,
    });
  }

  const { productPrices: courseraPlusMonthlyAndAnnualProductPrices } = useGetProductPrices({ products, skip });

  const {
    data: { courseraPlusMonthlyAndAnnualPricesByCurrency },
  } = useNaptime<PropsFromProductPrices>(() => {
    if (skip) {
      return {};
    }

    const productListWithCurrencyCode = products.map((product) => ({ ...product, ...currencyData }));

    return {
      courseraPlusMonthlyAndAnnualPricesByCurrency: shouldFetchPriceByCurrency
        ? ProductPricesV3.getPricesByCurrencyCodeAndCountry(productListWithCurrencyCode)
        : undefined,
    };
  }, [liteSubscription?.[0]?.id, cart?.id, shouldFetchPriceByCurrency, products?.length]);

  // If the prices by specified currency are not available, fallback to the default prices/currency
  const allProductPrices = courseraPlusMonthlyAndAnnualPricesByCurrency ?? courseraPlusMonthlyAndAnnualProductPrices;

  return {
    courseraPlusMonthlyProductPrice: allProductPrices?.[0],
    ...(shouldGetCourseraPlusAnnual ? { courseraPlusAnnualProductPrice: allProductPrices?.[1] } : {}),
  };
};
